<script>
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { mapGetters } from 'vuex';
import validations from './helpers/validations';

import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import InputNext from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import ButtonNext from 'next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SelectNext from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    InputNext,
    TextArea,
    ButtonNext,
    Avatar,
    SelectNext,
    Icon,
  },
  props: {
    bot: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: '',
      name: '',
      description: '',
      displayName: '',
      responseDelayDuration: 0,
      autoResolveDuration: 0,
      followupEnabled: false,
      followupTaskId: '',
      maxFollowupAttempts: 2,
      followupInstructions: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'wgptBots/getUIFlags',
      tasks: 'wgptTasks/getTasks',
      accountId: 'getCurrentAccountId',
    }),
    followupTaskOptions() {
      const autoOption = {
        value: '',
        label: this.$t(
          'WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.TASK.AUTO_CREATE'
        ),
      };
      const taskOptions = this.tasks
        .filter(task => task.type === 'bot_followup_task')
        .map(task => ({
          value: task.id,
          label: task.name || `Task #${task.id}`,
        }));
      return [autoOption, ...taskOptions];
    },
    followupTaskMissing() {
      if (!this.followupTaskId) return false;
      return !this.followupTaskOptions.some(
        opt => opt.value === this.followupTaskId
      );
    },
    followupTaskEditorUrl() {
      if (!this.followupTaskId || this.followupTaskMissing) return null;
      return frontendURL(
        `accounts/${this.accountId}/settings/wgpt-tasks/${this.followupTaskId}/edit`
      );
    },
  },
  watch: {
    bot: {
      handler() {
        this.setDefaults();
      },
      immediate: true,
    },
  },
  mounted() {
    if (!this.tasks.length) {
      this.$store.dispatch('wgptTasks/get');
    }
  },
  validations,
  methods: {
    setDefaults() {
      this.avatarUrl = this.bot.avatar_url;
      this.name = this.bot.name;
      this.displayName = this.bot.display_name;
      this.description = this.bot.description;
      this.responseDelayDuration = this.bot.config?.responseDelayDuration;
      this.autoResolveDuration = this.bot.config?.autoResolveDuration;
      this.followupEnabled = this.bot.config?.followupEnabled || false;
      this.followupTaskId = this.bot.config?.followupTaskId || '';
      this.maxFollowupAttempts = this.bot.config?.maxFollowupAttempts || 2;
      this.followupInstructions = this.bot.config?.followupInstructions || '';
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch('wgptBots/deleteAvatar', this.bot.id);
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(
          this.$t(
            'WGPT_BOTS.EDIT.TABS.BASIC.FORM.AVATAR.DELETE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t(
                'WGPT_BOTS.EDIT.TABS.BASIC.FORM.AVATAR.DELETE.API.ERROR_MESSAGE'
              )
        );
      }
    },
    async updateBot() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      try {
        const payload = {
          id: this.bot.id,
          name: this.name,
          display_name: this.displayName,
          description: this.description,
          config: {
            responseDelayDuration: Number(this.responseDelayDuration) || null,
            autoResolveDuration: Number(this.autoResolveDuration) || null,
            followupEnabled: this.followupEnabled,
            followupTaskId: Number(this.followupTaskId) || null,
            maxFollowupAttempts: Number(this.maxFollowupAttempts) || 2,
            followupInstructions: this.followupInstructions || null,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
          payload.isFormData = true;
        }
        await this.$store.dispatch('wgptBots/update', payload);
        this.setDefaults();
        this.$store.dispatch('wgptTasks/get');
        useAlert(this.$t('WGPT_BOTS.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('WGPT_BOTS.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col md:flex-row items-center lg:items-start justify-between gap-5 lg:gap-10 mx-6"
  >
    <div class="flex-1 flex flex-col min-w-0 max-w-4xl">
      <div class="flex flex-col gap-1 items-start mb-4">
        <label class="text-heading-3 text-n-slate-12">
          {{ $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.AVATAR.LABEL') }}
        </label>
        <Avatar
          :src="avatarUrl"
          :size="64"
          icon-name="i-lucide-building-2"
          name=""
          allow-upload
          rounded-full
          @upload="handleImageUpload"
          @delete="handleAvatarDelete"
        />
      </div>

      <SettingsFieldSection
        :label="$t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.NAME.LABEL')"
      >
        <woot-input
          v-model.trim="name"
          class="[&>input]:!mb-0"
          :class="{ error: v$.name.$error }"
          :placeholder="$t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.NAME.PLACEHOLDER')"
          :error="
            v$.name.$error
              ? $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.NAME.ERROR')
              : ''
          "
          @blur="v$.name.$touch"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.DISPLAY_NAME.LABEL')"
      >
        <woot-input
          v-model.trim="displayName"
          class="[&>input]:!mb-0"
          :class="{ error: v$.displayName.$error }"
          :placeholder="
            $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.DISPLAY_NAME.PLACEHOLDER')
          "
          :error="
            v$.displayName.$error
              ? $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.DISPLAY_NAME.ERROR')
              : ''
          "
          @blur="v$.displayName.$touch"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.DESCRIPTION.LABEL')"
      >
        <TextArea
          v-model.trim="description"
          type="text"
          custom-text-area-class="!outline !outline-0"
          :placeholder="
            $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.DESCRIPTION.PLACEHOLDER')
          "
          :message="
            v$.description.$error
              ? $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.DESCRIPTION.ERROR')
              : ''
          "
          :message-type="v$.description.$error ? 'error' : 'info'"
          @blur="v$.description.$touch"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="
          $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.RESPONSE_DELAY_DURATION.LABEL')
        "
      >
        <InputNext
          v-model.trim="responseDelayDuration"
          type="number"
          :placeholder="
            $t(
              'WGPT_BOTS.EDIT.TABS.BASIC.FORM.RESPONSE_DELAY_DURATION.PLACEHOLDER'
            )
          "
          :message="
            v$.responseDelayDuration.$error
              ? $t(
                  'WGPT_BOTS.EDIT.TABS.BASIC.FORM.RESPONSE_DELAY_DURATION.ERROR'
                )
              : $t(
                  'WGPT_BOTS.EDIT.TABS.BASIC.FORM.RESPONSE_DELAY_DURATION.INFO'
                )
          "
          :message-type="v$.responseDelayDuration.$error ? 'error' : 'info'"
          @blur="v$.responseDelayDuration.$touch"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="
          $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.AUTO_RESOLVE_DURATION.LABEL')
        "
      >
        <InputNext
          v-model.trim="autoResolveDuration"
          type="number"
          :placeholder="
            $t(
              'WGPT_BOTS.EDIT.TABS.BASIC.FORM.AUTO_RESOLVE_DURATION.PLACEHOLDER'
            )
          "
          :message="
            v$.autoResolveDuration.$error
              ? $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.AUTO_RESOLVE_DURATION.ERROR')
              : $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.AUTO_RESOLVE_DURATION.INFO')
          "
          :message-type="v$.autoResolveDuration.$error ? 'error' : 'info'"
          @blur="v$.autoResolveDuration.$touch"
        />
      </SettingsFieldSection>

      <SettingsToggleSection
        v-model="followupEnabled"
        :header="$t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.TOGGLE.LABEL')"
        :description="
          $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.TOGGLE.DESCRIPTION')
        "
        class="mt-4"
      >
        <template v-if="followupEnabled" #editor>
          <div class="flex flex-col gap-4">
            <SettingsFieldSection
              :label="$t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.TASK.LABEL')"
            >
              <div class="flex items-center gap-2">
                <SelectNext
                  v-model="followupTaskId"
                  :options="followupTaskOptions"
                />
                <a
                  v-if="followupTaskEditorUrl"
                  :href="followupTaskEditorUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-n-slate-11 hover:text-n-blue-9 transition-colors"
                  :title="
                    $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.TASK.EDIT_TASK')
                  "
                >
                  <Icon icon="i-lucide-external-link" class="size-4" />
                </a>
              </div>
              <p
                v-if="followupTaskMissing"
                class="flex items-center gap-1.5 text-xs text-n-ruby-11 mt-1"
              >
                <Icon icon="i-lucide-triangle-alert" class="size-3.5" />
                {{
                  $t(
                    'WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.TASK.MISSING_WARNING'
                  )
                }}
              </p>
            </SettingsFieldSection>
            <InputNext
              v-model.trim="maxFollowupAttempts"
              type="number"
              :label="
                $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.MAX_ATTEMPTS.LABEL')
              "
              :message="
                $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.MAX_ATTEMPTS.INFO')
              "
              message-type="info"
            />
            <TextArea
              v-model.trim="followupInstructions"
              custom-text-area-class="!outline !outline-0"
              :label="
                $t('WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.INSTRUCTIONS.LABEL')
              "
              :placeholder="
                $t(
                  'WGPT_BOTS.EDIT.TABS.BASIC.FORM.FOLLOWUP.INSTRUCTIONS.PLACEHOLDER'
                )
              "
              rows="4"
            />
          </div>
        </template>
      </SettingsToggleSection>

      <div class="w-full flex justify-end items-center py-4 mt-2">
        <ButtonNext
          type="submit"
          :disabled="v$.$invalid || uiFlags.isUpdating"
          :label="$t('WGPT_BOTS.EDIT.SUBMIT')"
          :is-loading="uiFlags.isUpdating"
          @click="updateBot"
        />
      </div>
    </div>
  </div>
</template>
